import React from 'react';
import { Lock } from 'lucide-react';
import { useFeatures } from '../contexts/FeaturesContext';

/**
 * Garde d'affichage sur la licence de l'école.
 *
 *   <FeatureGate cle="module.cantine">
 *     <Cantine />
 *   </FeatureGate>
 *
 * ⚠️ Ce composant cache, il ne protège pas. La protection est le middleware
 * `requireFeature` côté serveur : un utilisateur qui force l'URL reçoit un 403,
 * quoi qu'affiche cette page. Ne jamais s'appuyer sur `FeatureGate` seul pour
 * garder une donnée.
 *
 * Trois usages :
 *
 *   - sans `fallback` : affiche l'écran « module non inclus », pour une route
 *     entière que l'utilisateur a atteinte malgré le menu ;
 *   - `fallback={null}` : masque en silence, pour un bouton ou un onglet ;
 *   - `fallback={<Autre />}` : remplace par autre chose.
 */

const ModuleNonInclus = ({ libelle }) => (
  <div className="flex items-center justify-center px-6 py-20">
    <div className="max-w-md w-full text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#246990]/10 flex items-center justify-center mb-6">
        <Lock size={28} className="text-[#246990]" />
      </div>

      <h2 className="text-xl font-semibold text-gray-900 mb-3">
        Module non inclus dans votre licence
      </h2>

      <p className="text-gray-600 leading-relaxed">
        Le module <strong>{libelle}</strong> n'est pas activé pour votre
        établissement. Rapprochez-vous de votre administrateur pour faire
        évoluer votre abonnement.
      </p>
    </div>
  </div>
);

const FeatureGate = ({ cle, cles, children, fallback }) => {
  const { estActive, auMoinsUne, libelle } = useFeatures();

  // `cles` (plusieurs) l'emporte sur `cle` (une seule) : un écran qui couvre
  // deux modules — la comptabilité et la paie, par exemple — s'ouvre dès que
  // l'un des deux est souscrit.
  const ouvert = Array.isArray(cles) && cles.length > 0
    ? auMoinsUne(cles)
    : estActive(cle);

  if (ouvert) return children;
  if (fallback !== undefined) return fallback;

  const cleAffichee = Array.isArray(cles) && cles.length > 0 ? cles[0] : cle;
  return <ModuleNonInclus libelle={libelle(cleAffichee)} />;
};

export default FeatureGate;
